import React, { useEffect, useState } from "react";
import { platformAPI } from "../services/api";

/**
 * Platform-wide switches. Signup starts closed: opening the platform to
 * strangers is a decision, taken here, once there is something for them to
 * start from.
 */
export default function PlatformSettings() {
  const [settings, setSettings] = useState(null);
  const [plans, setPlans] = useState([]);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [methodsText, setMethodsText] = useState(null);

  useEffect(() => {
    Promise.all([platformAPI.getSettings(), platformAPI.listPlans()])
      .then(([{ data }, { data: planList }]) => {
        setSettings(data);
        setPlans(planList.filter((plan) => plan.is_active));
      })
      .catch((err) => setError(err.response?.data?.error || "Failed to load settings"));
  }, []);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setSaved(false);

    try {
      const { data } = await platformAPI.updateSettings({
        signup_enabled: settings.signup_enabled,
        signup_plan_id: settings.signup_plan_id || null,
        trial_days: Number(settings.trial_days),
        operator_email: settings.operator_email || null,
        grace_days: Number(settings.grace_days),
        payment_methods: String(methodsText ?? settings.payment_methods.join(", "))
          .split(",")
          .map((m) => m.trim().toLowerCase().replace(/\s+/g, "_"))
          .filter(Boolean),
        payment_instructions: settings.payment_instructions,
        whatsapp_number: settings.whatsapp_number || null,
        company_name: settings.company_name || null,
        company_address: settings.company_address || null,
        company_tax_id: settings.company_tax_id || null,
        company_phone: settings.company_phone || null,
        vat_rate: Number(settings.vat_rate) || 0,
        default_document_type: settings.default_document_type,
        receipt_footer: settings.receipt_footer || null,
      });
      setMethodsText(null);
      setSettings(data);
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return <p className="text-gray-500">{error || "Loading…"}</p>;

  return (
    <div className="max-w-xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Platform settings</h1>

      <form onSubmit={save} className="bg-white rounded-xl shadow p-5 space-y-5">
        <h2 className="font-semibold text-gray-800">Signup</h2>

        {error && <p className="text-red-600 text-sm">{error}</p>}
        {saved && <p className="text-green-700 text-sm">Saved.</p>}

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5"
            checked={Boolean(settings.signup_enabled)}
            onChange={(e) => setSettings({ ...settings, signup_enabled: e.target.checked })}
          />
          <span>
            <span className="font-medium text-gray-800">Anyone can sign up</span>
            <span className="block text-sm text-gray-500">
              Shows “Create a shop” on the sign-in page and accepts new signups. Leave it off until the product
              catalogue is ready.
            </span>
          </span>
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Plan given to new shops</span>
          <select
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={settings.signup_plan_id || ""}
            onChange={(e) => setSettings({ ...settings, signup_plan_id: e.target.value })}
          >
            <option value="">No plan (all modules, 3 devices)</option>
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Free trial length (days)</span>
          <input
            type="number"
            min="1"
            max="365"
            className="mt-1 w-32 px-3 py-2 border border-gray-300 rounded-lg"
            value={settings.trial_days}
            onChange={(e) => setSettings({ ...settings, trial_days: e.target.value })}
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Email me about new signups</span>
          <input
            type="email"
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="you@example.com"
            value={settings.operator_email || ""}
            onChange={(e) => setSettings({ ...settings, operator_email: e.target.value })}
          />
        </label>

        <h2 className="font-semibold text-gray-800 pt-4 border-t">Payments and receipts</h2>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Grace period after the due date (days)</span>
          <input
            type="number" min="0" max="60"
            className="mt-1 w-32 px-3 py-2 border border-gray-300 rounded-lg"
            value={settings.grace_days}
            onChange={(e) => setSettings({ ...settings, grace_days: e.target.value })}
          />
          <span className="block text-sm text-gray-500">
            How long a shop stays usable once its payment is overdue, before it is locked. Manual payments
            take days, not hours.
          </span>
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Payment methods you accept</span>
          <input
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={methodsText ?? settings.payment_methods.join(", ")}
            onChange={(e) => setMethodsText(e.target.value)}
          />
          <span className="block text-sm text-gray-500">Comma-separated: cash, d17, flouci, bank_transfer, card, other</span>
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">WhatsApp number (shown to owners)</span>
          <input
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="+216 20 123 456"
            value={settings.whatsapp_number || ""}
            onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
          />
        </label>

        {[["en", "English"], ["fr", "Français"], ["ar", "العربية"]].map(([lang, name]) => (
          <label key={lang} className="block">
            <span className="text-sm font-medium text-gray-700">How to pay — {name}</span>
            <textarea
              rows={3}
              dir={lang === "ar" ? "rtl" : "ltr"}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Bank account, D17 number, where to pay in cash…"
              value={settings.payment_instructions?.[lang] || ""}
              onChange={(e) =>
                setSettings({ ...settings, payment_instructions: { ...settings.payment_instructions, [lang]: e.target.value } })
              }
            />
          </label>
        ))}

        <h2 className="font-semibold text-gray-800 pt-4 border-t">Company on the receipt</h2>

        {[
          ["company_name", "Company name"],
          ["company_address", "Address"],
          ["company_phone", "Phone"],
          ["company_tax_id", "Tax ID (needed for invoices)"],
          ["receipt_footer", "Footer line"],
        ].map(([key, label]) => (
          <label key={key} className="block">
            <span className="text-sm font-medium text-gray-700">{label}</span>
            <input
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={settings[key] || ""}
              onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
            />
          </label>
        ))}

        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">VAT included in prices (%)</span>
            <input
              type="number" min="0" max="100" step="0.01"
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={settings.vat_rate}
              onChange={(e) => setSettings({ ...settings, vat_rate: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Document by default</span>
            <select
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={settings.default_document_type}
              onChange={(e) => setSettings({ ...settings, default_document_type: e.target.value })}
            >
              <option value="receipt">Receipt</option>
              <option value="invoice">Invoice</option>
            </select>
          </label>
        </div>
        <p className="text-sm text-gray-500">
          Receipts keep the details they were issued with: changing these affects only the next ones.
        </p>

        <button
          type="submit"
          disabled={busy}
          className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
        >
          Save settings
        </button>
      </form>
    </div>
  );
}
